/**
 * A árvore de serviços, achatada, para a avaliação NO NAVEGADOR.
 *
 * ## Por que isto existe
 *
 * Porque o filtro por serviço é avaliado em DOIS lugares. O servidor aplica
 * a condição na consulta — é ele que pagina — e a tela reavalia as mesmas
 * condições sobre as linhas já carregadas, para a busca e a mesclagem.
 *
 * E o servidor, ao filtrar por um serviço, inclui **toda a descendência**
 * dele: a árvore tem quatro níveis e os chamados ficam quase todos nas
 * folhas, então "Suporte" tem de significar "Suporte e tudo embaixo". Se a
 * tela não souber disso, o servidor devolve as linhas certas e a tela
 * descarta todas — "nenhum ticket encontrado" com o filtro funcionando
 * perfeitamente por baixo.
 *
 * Esse desfecho não é hipótese: foi exatamente o que aconteceu com os
 * valores dinâmicos de filtro em 08/10, e só apareceu no teste em tela.
 *
 * ## O que a linha do chamado traz, e por que não basta
 *
 * O payload manda `service:id,name,parent_id` e `service.parent:id,name` —
 * ou seja, UM nível de pai. Com quatro níveis, escolher um serviço de topo
 * deixaria o bisneto de fora aqui e dentro no servidor. Por isso a
 * ancestralidade sai da árvore inteira, que a tela já busca para a ação
 * "Alterar serviço".
 */

/** Os filhos de um nó, nas duas chaves em que a api já os entregou. */
const filhosDe = (servico) => servico?.subservices_tree || servico?.subservices || [];

/**
 * Mapa `id -> parent_id` a partir da árvore que `GET /services` devolve.
 *
 * @param {Array} arvore
 * @returns {Map<number, number|null>}
 */
export const paiPorServico = (arvore) => {
    const mapa = new Map();

    const visitar = (nos, paiId) => {
        (Array.isArray(nos) ? nos : []).forEach((no) => {
            if (no?.id == null) return;

            const id = Number(no.id);

            // Guarda contra ciclo e contra o mesmo nó aparecendo duas vezes
            // na árvore: sem isto, `ancestraisDe` abaixo giraria para sempre.
            if (mapa.has(id)) return;

            mapa.set(id, paiId);
            visitar(filhosDe(no), id);
        });
    };

    visitar(arvore, null);

    return mapa;
};

/**
 * O serviço e todos os ancestrais dele, do mais próximo ao mais distante.
 *
 * @param {number|string|null} id
 * @param {Map<number, number|null>} paiPorId
 * @returns {number[]} começa pelo próprio id
 */
export const ancestraisDe = (id, paiPorId) => {
    const inicio = Number(id);

    if (Number.isNaN(inicio)) return [];

    const cadeia = [inicio];
    const vistos = new Set([inicio]);
    let atual = paiPorId instanceof Map ? paiPorId.get(inicio) : null;

    // O teto é o mesmo do servidor (`Service::idsDaSubarvore`), pelo mesmo
    // motivo: nada no cadastro impede `parent_id` em ciclo.
    for (let i = 0; i < 10 && atual != null; i++) {
        const pai = Number(atual);

        if (vistos.has(pai)) break;

        cadeia.push(pai);
        vistos.add(pai);
        atual = paiPorId.get(pai);
    }

    return cadeia;
};

/**
 * O chamado está no serviço escolhido ou em algum descendente dele?
 *
 * Em vez de descer a árvore a partir do escolhido, SOBE a partir do chamado
 * e pergunta se o escolhido aparece. Dá a mesma resposta e custa o tamanho
 * da cadeia (no máximo quatro), não o tamanho da subárvore — que em
 * "Suporte" são dezenas de nós, por linha da lista.
 *
 * @param {object} ticket
 * @param {number|string} escolhido
 * @param {Map<number, number|null>} paiPorId
 */
export const chamadoNoServico = (ticket, escolhido, paiPorId) => {
    const doChamado = ticket?.service_id ?? ticket?.service?.id ?? null;

    if (doChamado == null) return false;

    const alvo = Number(escolhido);

    if (Number.isNaN(alvo)) return false;

    return ancestraisDe(doChamado, paiPorId).includes(alvo);
};
